import {
  PanelShell, Badge, ApiResourceView, tokens, TrackMetaChips,
  DJSEMApiClient, useApiResource,
  buildSequentialEdges, riskSummary,
  createDefaultSections, sectionRollup,
  type DjoidTrackRow,
  buildStemLanes, stemAwareTransitionSummary, type TrackStemAvailability as StemSetTrackAvailability,
} from "../index";

interface SetListItem { id: string; title: string; reviewStatus: string; }

/**
 * Sets module — lists the user's sets with review status, followed by the transition-graph
 * risk summary, section set builder, and stem-lane panels.
 *
 * @param client - The API client used to fetch the sets list.
 * @returns A panel of sets, plus the set graph, section builder, and stem lane panels.
 */
export function SetsModule({ client }: { client: DJSEMApiClient }) {
  const res = useApiResource(() => client.listSets(), [], (d) => (d.items?.length ?? 0) === 0);
  return (
    <ApiResourceView
      resource={res}
      empty={{ title: "No sets yet", detail: "Build a set from your library, then run the AI Set Coach on it." }}
      render={(data) => {
        const sets = (data.items ?? []) as unknown as SetListItem[];
        return (
          <>
            <PanelShell title="Your sets">
              {sets.map((s) => (
                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                  <span>{s.title}</span>
                  <Badge tone={s.reviewStatus === "approved" ? "ok" : "neutral"}>{s.reviewStatus}</Badge>
                </div>
              ))}
            </PanelShell>
            <SetGraphPanel />
            <SetBuilderPanel />
          </>
        );
      }}
    />
  );
}

// Prompt 93 — graph relationship summary. Uses the shared djoidGraphModel to score the sequential
// transitions of a set and roll them into a smooth/caution/risky tally (the canvas renders the
// full graph; this compact panel is the at-a-glance risk read shown on both web and desktop).
// Demo tracks shared by SetGraphPanel/SetBuilderPanel's stem-lane panel below — this whole
// panel has no real per-set track data wired yet (sections start empty; see SetBuilderPanel),
// so both use the same illustrative four-track set. The *computations* (risk, stem-aware
// warnings) are real, run through the actual model functions — only the underlying tracks are
// a placeholder, consistent with the rest of this panel today.
const SET_GRAPH_DEMO_TRACKS: DjoidTrackRow[] = [
  { id: "a", title: "Opener", artist: "—", bpm: 122, energy: 0.4, camelot: "8A" } as DjoidTrackRow,
  { id: "b", title: "Builder", artist: "—", bpm: 124, energy: 0.55, camelot: "8A" } as DjoidTrackRow,
  { id: "c", title: "Peak", artist: "—", bpm: 126, energy: 0.8, camelot: "9A" } as DjoidTrackRow,
  { id: "d", title: "Curveball", artist: "—", bpm: 140, energy: 0.5, camelot: "3B" } as DjoidTrackRow,
];
// Demo approved-stem availability (prompt 137) — only approved stems appear by default (§5).
const SET_GRAPH_DEMO_STEMS: StemSetTrackAvailability[] = [
  { trackId: "a", approvedStemTypes: ["vocals", "drums"] },
  { trackId: "b", approvedStemTypes: ["vocals", "bass"] },
  { trackId: "c", approvedStemTypes: ["drums", "bass"] },
  { trackId: "d", approvedStemTypes: [] },
];

function SetGraphPanel() {
  const summary = riskSummary(buildSequentialEdges(SET_GRAPH_DEMO_TRACKS));
  return (
    <PanelShell title="Transition graph (risk)" style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 8, fontSize: 12 }}>
        <Badge tone="ok">smooth {summary.smooth}</Badge>
        <Badge tone="neutral">caution {summary.caution}</Badge>
        <Badge tone="warn">risky {summary.risky}</Badge>
      </div>
      {/* Task #22: the demo tracks already carry bpm/energy/camelot (used above only for
          aggregate risk scoring) — show them per-track now, Djoid's own convention for
          track metadata at a glance. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
        {SET_GRAPH_DEMO_TRACKS.map((t) => (
          <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: tokens.color.text }}>{t.title}</span>
            <TrackMetaChips bpm={t.bpm} energy={t.energy} camelot={t.camelot} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: tokens.color.textDim, marginTop: 10 }}>
        Open the set graph to inspect and reorder risky transitions.
      </div>
    </PanelShell>
  );
}

// Prompt 94 — section set builder roll-up. Uses the shared djoidSetBuilderModel to show the
// default six-lane structure with per-lane counts/duration (the full builder has drag lanes +
// Track Explorer; this panel is the shared summary that renders identically on web and desktop).
function SetBuilderPanel() {
  const sections = createDefaultSections();
  const byId: Record<string, DjoidTrackRow> = {};
  return (
    <>
      <PanelShell title="Section set builder" style={{ marginTop: 12 }}>
        {sections.map((sec) => {
          const r = sectionRollup(sec, byId);
          return (
            <div key={sec.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0" }}>
              <span>{sec.title}</span>
              <span style={{ color: tokens.color.textDim }}>{r.trackCount} tracks</span>
            </div>
          );
        })}
      </PanelShell>
      <StemLanesPanel />
    </>
  );
}

/**
 * Stem lanes + stem-aware transition warnings (Prompt 137). Only approved stems show by
 * default. Warnings name what to check (vocal/bass/drum overlap risk) rather than claiming
 * exact-timed overlap detection — see stemSetLanesModel.ts's honesty note.
 */
function StemLanesPanel() {
  const lanes = buildStemLanes(SET_GRAPH_DEMO_STEMS);
  const summary = stemAwareTransitionSummary(SET_GRAPH_DEMO_STEMS);
  return (
    <PanelShell title="Stem lanes" style={{ marginTop: 12 }}>
      {lanes.map((lane) => (
        <div key={lane.stemType} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0" }}>
          <span>{lane.label}</span>
          <span style={{ color: tokens.color.textDim, fontSize: 11 }}>
            {SET_GRAPH_DEMO_STEMS.filter((t) => lane.availability[t.trackId]).length} of {SET_GRAPH_DEMO_STEMS.length} tracks
          </span>
        </div>
      ))}
      <div style={{ fontSize: 11, color: tokens.color.textDim, marginTop: 8 }}>
        {summary.totalWarnings > 0
          ? `${summary.totalWarnings} stem-aware warning${summary.totalWarnings === 1 ? "" : "s"} across ${summary.pairsWithWarnings} transition${summary.pairsWithWarnings === 1 ? "" : "s"} — review before finalizing the order.`
          : "No stem-aware warnings for the current order."}
      </div>
    </PanelShell>
  );
}
